import { useQuery } from '@tanstack/react-query'
import { API_BASE } from '../data/constants.js'

/**
 * useLinkPreview — fetch OG/Twitter meta for a URL via the worker.
 *
 * Server-side caching (via KV) means we can be very relaxed on the client:
 * no polling, long stale time. If the preview fetch fails or returns
 * nothing useful, the component just falls back to the bare link — we
 * never block the message itself.
 *
 * Query key is the URL itself, so two messages citing the same link
 * share a single cache entry.
 */
export function useLinkPreview(url) {
  return useQuery({
    queryKey: ['link-preview', url],
    queryFn: () => fetchLinkPreview(url),
    enabled: !!url,
    staleTime: 24 * 60 * 60 * 1000, // 24h — OG tags rarely change
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  })
}

async function fetchLinkPreview(url) {
  const res = await fetch(
    `${API_BASE}/link-preview?url=${encodeURIComponent(url)}`
  )
  if (!res.ok) {
    // 204 = worker explicitly found nothing useful
    if (res.status === 204) return null
    throw new Error(`Preview fetch failed: HTTP ${res.status}`)
  }
  return res.json()
}
